import { useRegionsApi } from '@/composables/useRegionsApi';
import { useUnitConfigurationsApi } from '@/composables/useUnitConfigurationsApi';
import { useUnitsApi } from '@/composables/useUnitsApi';
import { ListingTypeEnum } from '@/enums/ListingType';
import { UnitStatusEnum } from '@/enums/UnitStatus';
import type { Region } from '@/typings/regions/Region';
import type { GetUnitsRequest } from '@/typings/requests/GetUnitsRequest';
import type { Unit } from '@/typings/units/Unit';

type ToolExecuteParams = {
  listingType?: 'rental' | 'sale';
  regionName?: string;
  size?: string | string[];
  unitConfigurations?: string[];
  availabilityDates?: string[];
  dogsAllowed?: boolean;
  floor?: number | number[];
  minPrice?: number;
  maxPrice?: number;
  page?: number;
  pageSize?: number;
};

type ModelContextToolRegistration = {
  name: string;
  description: string;
  annotations?: {
    readOnlyHint?: boolean;
    untrustedContentHint?: boolean;
  };
  inputSchema?: Record<string, unknown>;
  execute: (params: ToolExecuteParams) => Promise<unknown> | unknown;
};

type ModelContextApi = {
  registerTool: (tool: ModelContextToolRegistration, options?: { signal?: AbortSignal }) => void;
};

type WebMcpToolResult = {
  success: boolean;
  message: string;
  appliedFilters?: Record<string, unknown>;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  units?: Array<{
    id: number;
    unitNumber: string;
    basePrice: number | null;
    city?: string;
    regionName?: string;
    projectName?: string;
    floorNo?: number;
    dogAllowed?: boolean;
    bedrooms?: number;
    availabilityDate?: Date;
    fullAddress?: string;
  }>;
};

const TOOL_NAME = 'search_housing_unit';

function getModelContextApi(): ModelContextApi | undefined {
  const fromNavigator = (navigator as unknown as { modelContext?: unknown }).modelContext;
  const fromDocument = (document as unknown as { modelContext?: unknown }).modelContext;
  const modelContext = fromNavigator ?? fromDocument;

  if (modelContext == null || typeof modelContext !== 'object') {
    return undefined;
  }

  const registerToolCandidate = (modelContext as { registerTool?: unknown }).registerTool;
  if (typeof registerToolCandidate !== 'function') {
    return undefined;
  }

  return {
    registerTool: registerToolCandidate.bind(modelContext) as ModelContextApi['registerTool']
  };
}

function mapListingTypes(listingType: ToolExecuteParams['listingType'] | undefined, currentPageListingTypes: ListingTypeEnum[] | undefined): ListingTypeEnum[] {
  if (listingType === 'sale') {
    return [ListingTypeEnum.Sale];
  }

  if (listingType === 'rental') {
    return [ListingTypeEnum.Rental];
  }

  if (currentPageListingTypes != null && currentPageListingTypes.length > 0) {
    return currentPageListingTypes;
  }

  return [ListingTypeEnum.Rental];
}

function normalizeFloorNumbers(floor: ToolExecuteParams['floor']): number[] | undefined {
  if (Array.isArray(floor)) {
    const normalized = floor.filter((value) => Number.isFinite(value));
    return normalized.length > 0 ? normalized : undefined;
  }

  if (typeof floor === 'number' && Number.isFinite(floor)) {
    return [floor];
  }

  return undefined;
}

function normalizeText(value: string): string {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function getRegionLabel(region: Region): string {
  return region.localizedName?.trim() || region.name.trim();
}

function buildRegionLookup(regions: Region[]): Map<string, number> {
  const lookup = new Map<string, number>();

  for (const region of regions) {
    const labels = [region.name, region.localizedName].filter((value): value is string => value != null);

    for (const label of labels) {
      const normalized = normalizeText(label);
      if (!lookup.has(normalized)) {
        lookup.set(normalized, region.id);
      }
    }
  }

  return lookup;
}

function resolveRegionId(regionName: string | undefined, regionLookup: Map<string, number>): number | undefined {
  if (regionName == null) {
    return undefined;
  }

  return regionLookup.get(normalizeText(regionName));
}

async function resolveUnitConfigurations(
  size: ToolExecuteParams['size'],
  explicitUnitConfigurations: ToolExecuteParams['unitConfigurations'],
  getAllUnitConfigurations: ReturnType<typeof useUnitConfigurationsApi>['getAllUnitConfigurations']
): Promise<string[] | undefined> {
  if (Array.isArray(explicitUnitConfigurations) && explicitUnitConfigurations.length > 0) {
    return explicitUnitConfigurations;
  }

  if (size == null) {
    return undefined;
  }

  const targetSizeValues = Array.isArray(size) ? size : [size];
  const normalizedTargets = targetSizeValues
    .map((value) => value.trim().toLowerCase())
    .filter((value) => value.length > 0);

  if (normalizedTargets.length === 0) {
    return undefined;
  }

  const configurations = await getAllUnitConfigurations();
  const matches = configurations
    .filter((configuration) => {
      const nameFr = configuration.name.trim().toLowerCase();
      const nameEn = configuration.nameEn.trim().toLowerCase();
      const idValue = configuration.id.toString();

      return normalizedTargets.some((target) => {
        return target === idValue || nameFr.includes(target) || nameEn.includes(target);
      });
    })
    .map((configuration) => configuration.id.toString());

  return matches.length > 0 ? matches : undefined;
}

function buildUrlParams(filters: GetUnitsRequest): URLSearchParams {
  const params = new URLSearchParams(window.location.search);

  const setSemicolonArray = (key: string, values?: Array<string | number>) => {
    if (values != null && values.length > 0) {
      params.set(key, values.join(';'));
      return;
    }
    params.delete(key);
  };

  setSemicolonArray('regionIds', filters.regionIds);
  setSemicolonArray('unitConfigurations', filters.unitConfigurations);
  setSemicolonArray('availabilityDates', filters.availabilityDates);
  setSemicolonArray('floors', filters.floorNumbers);

  if (filters.minPrice != null) {
    params.set('minPrice', filters.minPrice.toString());
  } else {
    params.delete('minPrice');
  }

  if (filters.maxPrice != null) {
    params.set('maxPrice', filters.maxPrice.toString());
  } else {
    params.delete('maxPrice');
  }

  if (filters.dogAllowed != null) {
    params.set('dogAllowed', filters.dogAllowed ? '1' : '0');
  } else {
    params.delete('dogAllowed');
  }

  return params;
}

function mapUnitsForToolResult(units: Unit[]): WebMcpToolResult['units'] {
  return units.map((unit) => ({
    id: unit.id,
    unitNumber: unit.unitNumber,
    basePrice: unit.basePrice,
    city: unit.city,
    regionName: unit.localizedRegionName ?? unit.regionName,
    projectName: unit.localizedProjectName ?? unit.projectName,
    floorNo: unit.floorNo,
    dogAllowed: unit.dogAllowed,
    bedrooms: unit.bedrooms,
    availabilityDate: unit.availabilityDate,
    fullAddress: unit.fullAddress
  }));
}

export function useWebMcpUnitsSearchTool(options: {
  listingTypes?: ListingTypeEnum[];
}) {
  const { listUnitsPaginated } = useUnitsApi();
  const { getAllRegions } = useRegionsApi();
  const { getAllUnitConfigurations } = useUnitConfigurationsApi();
  let registrationController: AbortController | null = null;

  const register = async () => {
    const modelContextApi = getModelContextApi();
    if (modelContextApi == null) {
      return;
    }

    unregister();
    const controller = new AbortController();
    registrationController = controller;

    const regions = await getAllRegions({
      unitStatuses: [UnitStatusEnum.Available, UnitStatusEnum.Reserved],
      locale: window.navigator.language.substring(0, 2)
    });

    if (controller.signal.aborted) {
      return;
    }

    const regionLookup = buildRegionLookup(regions);
    const allowedRegionNames = [...new Set(regions.map(getRegionLabel))];

    modelContextApi.registerTool(
      {
        name: TOOL_NAME,
        description: 'Search available condos/apartments based on user-selected criteria (size, dog policy, dates, floor, and price).',
        annotations: { readOnlyHint: false },
        inputSchema: {
          type: 'object',
          additionalProperties: false,
          properties: {
            listingType: {
              type: 'string',
              enum: ['rental', 'sale'],
              description: 'Search mode. Defaults to current page listing type.'
            },
            regionName: {
              type: 'string',
              enum: allowedRegionNames,
              description: 'Allowed region name.'
            },
            size: {
              oneOf: [
                { type: 'string' },
                { type: 'array', items: { type: 'string' } }
              ],
              description: 'Human-readable unit size (example: "4 1/2").'
            },
            unitConfigurations: {
              type: 'array',
              items: { type: 'string' },
              description: 'Unit configuration IDs as strings.'
            },
            availabilityDates: {
              type: 'array',
              items: { type: 'string' },
              description: 'Availability date values from existing filters.'
            },
            dogsAllowed: {
              type: 'boolean',
              description: 'Whether dogs are allowed.'
            },
            floor: {
              oneOf: [
                { type: 'number' },
                { type: 'array', items: { type: 'number' } }
              ],
              description: 'Floor number or floor numbers.'
            },
            minPrice: {
              type: 'number',
              description: 'Minimum price filter.'
            },
            maxPrice: {
              type: 'number',
              description: 'Maximum price filter.'
            },
            page: {
              type: 'number',
              description: 'Page number (default 1).'
            },
            pageSize: {
              type: 'number',
              description: 'Page size (default 20).'
            }
          }
        },
        execute: async (params): Promise<WebMcpToolResult> => {
          const listingTypes = mapListingTypes(params.listingType, options.listingTypes);
          const unitConfigurations = await resolveUnitConfigurations(params.size, params.unitConfigurations, getAllUnitConfigurations);
          const floorNumbers = normalizeFloorNumbers(params.floor);
          const regionId = resolveRegionId(params.regionName, regionLookup);

          if (params.regionName != null && regionId == null) {
            return {
              success: false,
              message: `Unsupported region: ${params.regionName}`
            };
          }

          const page = params.page != null && params.page > 0 ? Math.floor(params.page) : 1;
          const pageSize = params.pageSize != null && params.pageSize > 0 ? Math.floor(params.pageSize) : 20;

          const request: GetUnitsRequest & { page: number; pageSize: number } = {
            listingTypes,
            statuses: [UnitStatusEnum.Available],
            regionIds: regionId != null ? [regionId] : undefined,
            unitConfigurations,
            availabilityDates: params.availabilityDates,
            dogAllowed: listingTypes.includes(ListingTypeEnum.Rental) ? params.dogsAllowed : undefined,
            floorNumbers,
            minPrice: params.minPrice,
            maxPrice: params.maxPrice,
            page,
            pageSize
          };

          const result = await listUnitsPaginated(request);
          const _urlParams = buildUrlParams(request);

          return {
            success: true,
            message: `Found ${result.totalCount} matching units.`,
            appliedFilters: {
              listingTypes,
              regionIds: request.regionIds,
              unitConfigurations: request.unitConfigurations,
              availabilityDates: request.availabilityDates,
              dogAllowed: request.dogAllowed,
              floorNumbers: request.floorNumbers,
              minPrice: request.minPrice,
              maxPrice: request.maxPrice
            },
            totalCount: result.totalCount,
            page: result.currentPage,
            pageSize: result.pageSize,
            totalPages: result.totalPages,
            units: mapUnitsForToolResult(result.units)
          };
        }
      },
      { signal: controller.signal }
    );
  };

  const unregister = () => {
    if (registrationController != null) {
      registrationController.abort();
      registrationController = null;
    }
  };

  return {
    register,
    unregister
  };
}
